import React, { ForwardedRef, forwardRef } from "react";

import { cn } from "@/utils/cn";

export interface FormButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "add" | "outline" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  isLoading?: boolean;
  fullWidth?: boolean;
  className?: string;
  isBlackLoader?: boolean;
}

/**
 * FormButton component
 * Reusable button component with multiple variants and sizes
 */
const FormButton = (
  {
    children,
    variant = "primary",
    size = "md",
    startIcon,
    endIcon,
    isLoading = false,
    fullWidth = false,
    className,
    disabled,
    type = "button",
    isBlackLoader = false,
    ...props
  }: FormButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <button
      ref={ref}
      onClick={(e) => {
        if (props.onClick) {
          props.onClick(e);
        }
        setTimeout(() => {
          e.currentTarget.blur();
        }, 300)
      }}
      type={type}
      disabled={disabled || isLoading}
      className={cn(
        "form-button-base",
        {
          "form-button-primary": variant === "primary",
          "form-button-secondary": variant === "secondary",
          "form-button-add": variant === "add",
          "form-button-outline": variant === "outline",
          "form-button-ghost": variant === "ghost",
          "form-button-danger": variant === "danger",
          "form-button-sm": size === "sm",
          "form-button-md": size === "md",
          "form-button-lg": size === "lg",
          "form-button-loading": isLoading,
          "w-full": fullWidth,
        },
        className,
      )}
      {...props}
    >
      {isLoading && (
        <div className="inline-flex items-center">
          <div
            className={cn(
              "w-[1.2rem] h-[1.2rem] border-2  border-white/30 border-t-white rounded-full animate-spin",
              isBlackLoader && "!border-black !border-t-gray",
            )}
          />
        </div>
      )}
      {!isLoading && startIcon && (
        <span className="form-button-icon">{startIcon}</span>
      )}
      {children}
      {!isLoading && endIcon && (
        <span className="form-button-icon">{endIcon}</span>
      )}
    </button>
  );
};

export default forwardRef(FormButton);
